/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

import type { ContactStatus } from "@/hooks/useEmergencyContacts";

/**
 * THE GATE for the two-step opt-in: is this contact eligible to receive a
 * safety-event SMS (SOS / Walk Me Home)?
 *
 * 🔴 SAFETY-CRITICAL. A bug here can silently drop SOS SMS to real emergency
 * contacts. The rule is intentionally FAIL-SAFE by GRANDFATHERING:
 *
 *   - status === undefined  → ELIGIBLE  (legacy record, never migrated — always
 *                                         still alerted; see the two-step design)
 *   - status === "confirmed" → ELIGIBLE (recipient explicitly replied YES)
 *   - status === "pending"   → NOT eligible (invited, hasn't replied YES)
 *   - status === "declined"  → NOT eligible (recipient replied STOP)
 *
 * i.e. ONLY explicit "pending"/"declined" are excluded. Any ambiguity (missing,
 * null, unknown string) defaults to ELIGIBLE, so we never fail CLOSED on a
 * safety path. New adds always carry an explicit "pending", so a legacy
 * undefined can only mean a genuinely pre-two-step contact.
 */
export function eligibleForSms(status: ContactStatus | undefined | null): boolean {
  return status !== "pending" && status !== "declined";
}

/** Filter any list of status-bearing contacts to the SMS-eligible ones. */
export function filterEligibleContacts<T extends { status?: ContactStatus }>(
  contacts: T[],
): T[] {
  return contacts.filter((c) => eligibleForSms(c.status));
}
